/**
 * Keeps link credentials out of telemetry.
 *
 * Two kinds of address carry a bearer token: a patient's cancel link and a
 * guest's meeting join link. Whoever reads the token can act on that
 * appointment, and a URL next to an IP address says someone is in therapy —
 * so neither may reach Sentry as-is. Fragments are dropped wholesale: the
 * cancel link carries its token there, and nothing we monitor needs one.
 */

const SECRET_SEGMENTS: RegExp[] = [
    // `/appointments/cancel/<token>` — the old link shape, and its API twin.
    // `lookup` is an endpoint name, not a token.
    /(\/appointments\/cancel\/)(?!lookup(?=[/?#\s]|$))[^/?#\s]+/g,
    // `/meet/j/<token>` — a guest's join link. `/meet/<id>` is the therapist's
    // own room page and carries no credential.
    /(\/meet\/j\/)[^/?#\s]+/g,
];

export function redactUrl(value: string): string {
    let redacted = value.replace(/#\S*/g, "");
    for (const pattern of SECRET_SEGMENTS) {
        redacted = redacted.replace(pattern, "$1[redacted]");
    }
    return redacted;
}

type StringBag = Record<string, unknown>;

/** The parts of a Sentry event that can hold a URL. */
interface RedactableEvent {
    request?: { url?: string };
    transaction?: string;
    breadcrumbs?: { data?: StringBag; message?: string }[];
    spans?: { description?: string; data?: StringBag }[];
}

function redactValues(bag: StringBag): void {
    for (const [key, value] of Object.entries(bag)) {
        if (typeof value === "string") bag[key] = redactUrl(value);
    }
}

/** Redacts, in place, every URL an error or transaction event carries. */
export function redactSentryEvent<T extends RedactableEvent>(event: T): T {
    if (event.request?.url) event.request.url = redactUrl(event.request.url);
    if (event.transaction) event.transaction = redactUrl(event.transaction);
    for (const breadcrumb of event.breadcrumbs ?? []) {
        if (breadcrumb.data) redactValues(breadcrumb.data);
        if (typeof breadcrumb.message === "string") {
            breadcrumb.message = redactUrl(breadcrumb.message);
        }
    }
    for (const span of event.spans ?? []) {
        if (span.description) span.description = redactUrl(span.description);
        if (span.data) redactValues(span.data);
    }
    return event;
}
